<template>
	<n-select
		v-model:value="modelValue"
		class="w-auto!"
		filterable
		remote
		clearable
		:consistent-menu-width="false"
		:loading="loadingPatients"
		:options="patientOptions"
		:placeholder="placeholder || $t(`appointments.common.all_patients`)"
		@search="debouncedSearchPatients"
		@update:show="handleSelectShow"
	/>
</template>

<script setup lang="ts">
import type { SelectOption } from "naive-ui"
import type { Patient } from "@/types/patient"
import { useDebounceFn } from "@vueuse/core"
import { NSelect } from "naive-ui"
import { ref, watch } from "vue"
import Api from "@/api"

defineProps<{
	placeholder?: string
}>()

const SEARCH_DEBOUNCE_MS = 300

const modelValue = defineModel<number | null>({ default: null })

const loadingPatients = ref(false)
const patientOptions = ref<SelectOption[]>([])

function patientToOption(patient: Patient): SelectOption {
	return {
		label: `${patient.name} ${patient.surname}`,
		value: patient.id
	}
}

function searchPatients(query: string) {
	loadingPatients.value = true

	const selectedOption =
		modelValue.value !== null ? patientOptions.value.find(item => item.value === modelValue.value) : undefined

	Api.patients
		.list({
			perPage: 20,
			page: 1,
			filters: query ? { text: query } : undefined
		})
		.then(res => {
			const options = res.data.items.map(patientToOption)

			if (selectedOption && !options.some(option => option.value === selectedOption.value)) {
				patientOptions.value = [selectedOption, ...options]
				return
			}

			patientOptions.value = options
		})
		.catch(() => {
			patientOptions.value = selectedOption ? [selectedOption] : []
		})
		.finally(() => {
			loadingPatients.value = false
		})
}

const debouncedSearchPatients = useDebounceFn(searchPatients, SEARCH_DEBOUNCE_MS)

function handleSelectShow(isOpen: boolean) {
	if (isOpen) {
		searchPatients("")
	}
}

function loadPatientOption(patientId: number) {
	Api.patients
		.details(patientId)
		.then(res => {
			const option = patientToOption(res.data)

			if (!patientOptions.value.some(item => item.value === option.value)) {
				patientOptions.value = [option, ...patientOptions.value]
			}
		})
		.catch(() => {})
}

watch(
	modelValue,
	patientId => {
		if (patientId != null && !patientOptions.value.some(option => option.value === patientId)) {
			loadPatientOption(patientId)
		}
	},
	{ immediate: true }
)
</script>
